import useCollection from '@/application/read/collections/useCollection';
import {
  getBestBuyPrice,
  getBestSellPrice,
} from '@/application/read/vaults/utils';
import { TokenInterface } from '@/core/tokens';
import Icon, { Icons } from '@/presentation/components/elements/Icon';
import Price from '@/presentation/components/elements/Price';
import { useNetwork } from '@/presentation/context/network';
import useImageColor from '@/presentation/hooks/useImageColor';
import { invertColor, rgbToHex } from '@/presentation/utils/color';
import { getCollectionPath } from '@/presentation/utils/paths';
import { useTransition, animated } from '@react-spring/web';
import Link from 'next/link';
import { Address, LiquidityPool, Vault, Zero } from 'nftx.js';
import React from 'react';
import { useMemo, useState } from 'react';
import { useTranslations } from 'use-intl';
import { CarouselButton } from './CarouselButton';

const CarouselItem = ({
  collectionAddress,
  vaults,
  pools,
  onNextClick,
  onPrevClick,
}: {
  collectionAddress: Address;
  vaults: Vault[];
  tokens: TokenInterface[];
  pools: LiquidityPool[];
  onNextClick: () => void;
  onPrevClick: () => void;
}) => {
  const { slug } = useNetwork();
  const t = useTranslations('Collections.Carousel');
  const { data } = useCollection(collectionAddress);

  const { data: color } = useImageColor(data?.image);

  const textColor =
    invertColor(rgbToHex(color?.raw[0], color?.raw[1], color?.raw[2]), true) ||
    undefined; // fallback to a light gray as the default empty image state is dark grey

  const bestBuyPrice = useMemo(() => {
    return getBestBuyPrice(vaults);
  }, [vaults]);

  const bestSellPrice = useMemo(() => {
    return getBestSellPrice(vaults);
  }, [vaults]);

  return (
    <div
      className={`p-2 rounded-3xl flex bg-gray-800 h-44 sm:h-56 lg:h-64 text-gray-50 relative ${
        !data && 'animate-pulse'
      }`}
      style={{
        backgroundImage: `linear-gradient(to top, rgba(${color?.raw[0]},${color?.raw[1]},${color?.raw[2]},0.7), rgba(${color?.raw[0]},${color?.raw[1]},${color?.raw[2]},0.9))`,
        color: textColor,
      }}
    >
      <Link
        href={getCollectionPath({
          chain: slug,
          collectionSlug: data?.slug ?? collectionAddress,
        })}
        className="absolute inset-0"
      />
      {data?.image ? (
        <img
          src={data.image}
          className="rounded-2xl overflow-hidden object-cover aspect-square hidden sm:block"
          alt={data.name}
        />
      ) : (
        <div className="rounded-2xl bg-gray-500 h-40 sm:h-52 lg:h-60 aspect-square hidden sm:block" />
      )}
      <CarouselButton onClick={onPrevClick} textColor={textColor}>
        <Icon
          name={Icons.ARROW_DOWN}
          className={`transform rotate-90`}
          size={16}
        />
      </CarouselButton>
      <div className="text-center p-1 sm:p-4 flex-1 flex items-center justify-center">
        <div className="w-full">
          <header className="overflow-hidden mx-auto max-w-[220px] sm:max-w-full">
            <span className="text-gray-800 text-xs bg-gray-100 rounded-sm p-1 uppercase truncate">
              {t('title')}
            </span>
            <h4 className={`text-2xl font-black mt-2 truncate`}>
              {data?.name}
            </h4>
          </header>

          <dl
            className={`md:grid-cols-3 text-center gap-4 mt-4 hidden md:grid`}
          >
            <div>
              <dt className={`text-base font-normal `}>{t('price')}</dt>
              <dd className="font-bold text-xl">
                {<Price value={bestBuyPrice} />}
              </dd>
            </div>
            <div>
              <dt className={`text-base font-normal`}>{t('sellPrice')}</dt>
              <dd className="font-bold text-xl">
                {<Price value={bestSellPrice} />}
              </dd>
            </div>
            <div>
              <dt className={`text-base font-normal`}>{t('volume')}</dt>
              <dd className="font-bold text-xl">
                {
                  <Price
                    value={pools?.reduce(
                      (total, pool) => total + pool.dailyVolume,
                      0n
                    )}
                    showEmpty
                  />
                }
              </dd>
            </div>
          </dl>
        </div>
      </div>
      <CarouselButton onClick={onNextClick} textColor={textColor}>
        <Icon
          name={Icons.ARROW_DOWN}
          className={`transform -rotate-90`}
          size={16}
        />
      </CarouselButton>
    </div>
  );
};

const Carousel = ({
  items = [],
}: {
  items?: {
    collectionAddress: Address;
    vaults: Vault[];
    tokens: TokenInterface[];
    pools: LiquidityPool[];
  }[];
}) => {
  const [index, set] = useState(0);
  const transitions = useTransition(index, {
    key: index,
    from: { opacity: 0.3 },
    enter: { opacity: 1 },
    leave: { opacity: 0.6 },
    config: { duration: 200 },
    exitBeforeEnter: true,
  });

  return transitions((style, i) => (
    <animated.div style={{ ...style }}>
      <CarouselItem
        {...items[i]}
        onNextClick={() => set((state) => (state + 1) % items.length)}
        onPrevClick={() =>
          set((state) => (state - 1 + items.length) % items.length)
        }
      />
    </animated.div>
  ));
};

export default Carousel;
